/**
 * Reports, Labels (migration 0090; the #55 plan's §D.1 and §D.5.3).
 *
 * ONE label, or one label and its family across years, in the period: every
 * posted line carrying it, ONE total, and the same amount broken down by
 * account. Several labels side by side do not add up, since a line can carry
 * more than one, so this screen never lists labels with amounts beside each
 * other and never prints a total across them (§D.1; `LabelReport.test.tsx`
 * holds it). Both halves of a reversal pair are left out by the API (LB-1).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, type LabelReport, type LabelYear } from '../api.js';
import { formatCents } from '../lib/money.js';
import { InfoNote } from './InfoNote.js';

export function LabelReportView(props: { from: string; to: string; word: string }) {
  const [year, setYear] = useState<number | undefined>(undefined);
  const [labels, setLabels] = useState<LabelYear | null>(null);
  const [labelId, setLabelId] = useState('');
  const [family, setFamily] = useState(false);
  const [report, setReport] = useState<LabelReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .listLabels(year)
      .then((result) => {
        if (!live) return;
        setLabels(result);
        setLabelId((current) => (result.labels.some((label) => label.id === current) ? current : (result.labels[0]?.id ?? '')));
      })
      .catch((caught: unknown) => live && setError(caught instanceof ApiError ? caught.message : 'Could not read the labels.'));
    return () => {
      live = false;
    };
  }, [year]);

  useEffect(() => {
    if (labelId === '') {
      setReport(null);
      return;
    }
    let live = true;
    setError(null);
    api
      .labelReport(labelId, family, props.from, props.to)
      .then((result) => live && setReport(result))
      .catch((caught: unknown) => live && setError(caught instanceof ApiError ? caught.message : 'Could not build the report.'));
    return () => {
      live = false;
    };
  }, [labelId, family, props.from, props.to]);

  const showFund = report?.lines.some((line) => line.fundCode !== null) ?? false;
  const showProject = report?.lines.some((line) => line.projectName !== null) ?? false;

  return (
    <div className="report">
      <h2>Labels</h2>
      <p className="muted">
        One label&rsquo;s lines and its total.{' '}
        <InfoNote label="Why One Label At A Time">
          A line can carry several labels, so labels side by side do not add up, and this report reads one at a time. Its
          family is the label and every label it was rolled over from or into, so one report can cover several years. An entry
          that was reversed is left out with its reversal.
        </InfoNote>
      </p>

      {labels !== null && (
        <div className="row report-period">
          <button type="button" className="link" onClick={() => setYear(labels.fiscalYear - 1)}>
            ‹ Fiscal {labels.fiscalYear - 1}
          </button>
          <label>
            Label{' '}
            <select value={labelId} onChange={(event) => setLabelId(event.target.value)} disabled={labels.labels.length === 0}>
              {labels.labels.length === 0 && <option value="">None in fiscal {labels.fiscalYear}</option>}
              {labels.labels.map((label) => (
                <option key={label.id} value={label.id}>
                  {label.name}
                  {label.archivedAt === null ? '' : ' (Archived)'}
                </option>
              ))}
            </select>
          </label>
          <label>
            Read{' '}
            <select value={family ? 'family' : 'one'} onChange={(event) => setFamily(event.target.value === 'family')}>
              <option value="one">This Label Only</option>
              <option value="family">This Label And Its Family</option>
            </select>
          </label>
          <button type="button" className="link" onClick={() => setYear(labels.fiscalYear + 1)}>
            Fiscal {labels.fiscalYear + 1} ›
          </button>
        </div>
      )}

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {report !== null && (
        <>
          <p className="muted">
            {report.label.name}
            {report.family && report.years.length > 1 ? `, fiscal ${report.years.join(', ')}` : `, fiscal ${report.label.fiscalYear}`}.
            {' '}From {props.from} to {props.to}.
          </p>
          {report.lines.length === 0 ? (
            <p className="muted">No posted line in the period carries this label.</p>
          ) : (
            <div className="table-scroll">
              <table className="lines">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Entry</th>
                    <th>Account</th>
                    {showFund && <th>Fund</th>}
                    {showProject && <th>{props.word}</th>}
                    {report.family && <th>Label</th>}
                    <th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {report.lines.map((line) => (
                    <tr key={line.lineId}>
                      <td>{line.entryDate}</td>
                      <td>
                        <Link to={`/journals/${line.headerId}`}>{line.description ?? 'Entry'}</Link>
                      </td>
                      <td>
                        {line.accountCode} {line.accountName}
                      </td>
                      {showFund && <td>{line.fundCode ?? ''}</td>}
                      {showProject && <td>{line.projectName ?? ''}</td>}
                      {report.family && <td>{`${line.labelName} (${line.labelYear})`}</td>}
                      <td className="num">{formatCents(line.amountCents)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="report-total">
                    <th colSpan={3 + (showFund ? 1 : 0) + (showProject ? 1 : 0) + (report.family ? 1 : 0)}>Total, Debits Less Credits</th>
                    <td className="num">{formatCents(report.totalCents)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          {report.byAccount.length > 1 && (
            <>
              <h3>By Account</h3>
              <div className="table-scroll">
                <table className="lines">
                  <tbody>
                    {report.byAccount.map((row) => (
                      <tr key={row.accountCode}>
                        <td>
                          {row.accountCode} {row.accountName}
                        </td>
                        <td className="num">{formatCents(row.amountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
